import {
  Alert,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageUploader from "../../Components/UI/ImageUploader";
import { instance } from "../../Const/ApiHeader";
import {
  BLOG_CATEGORIES,
  CREATE_BLOG,
  GET_BLOG,
  UPDATE_BLOG,
  UPLOAD_BLOG_URL,
} from "../../Const/ApiConst";

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

function BlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [tagsInput, setTagsInput] = useState("");
  const [status, setStatus] = useState("draft");

  useEffect(() => {
    const load = async () => {
      try {
        const categoriesRes = await instance.get(BLOG_CATEGORIES);
        setCategories(categoriesRes.data || []);

        if (!isEdit) return;

        setLoading(true);
        const postRes = await instance.get(GET_BLOG(id));
        const post = postRes.data;
        setTitle(post.title || "");
        setSlug(post.slug || "");
        setSlugTouched(true);
        setExcerpt(post.excerpt || "");
        setContent(post.content || "");
        setFeaturedImage(post.featuredImage || "");
        setSelectedCategories(
          (post.categories || []).map((c) => c._id || c)
        );
        setTagsInput((post.tags || []).join(", "));
        setStatus(post.status === "published" ? "published" : "draft");
      } catch (err) {
        console.log(err);
        setError("Failed to load blog post.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isEdit]);

  const pageTitle = isEdit ? "Edit Blog Post" : "New Blog Post";

  const tags = useMemo(
    () =>
      tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [tagsInput]
  );

  const toggleCategory = (categoryId) => {
    setSelectedCategories((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId]
    );
  };

  const buildPayload = (nextStatus) => ({
    title: title.trim(),
    slug: slug.trim() || slugify(title),
    excerpt: excerpt.trim(),
    content,
    featuredImage,
    categories: selectedCategories,
    tags,
    status: nextStatus,
    // Always request a reader push when publishing from the Publish button.
    sendPush: nextStatus === "published",
  });

  const savePost = async (nextStatus) => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }

    setSaveLoading(true);
    setError("");
    try {
      const payload = buildPayload(nextStatus);
      let response;
      if (isEdit) {
        response = await instance.put(UPDATE_BLOG(id), payload);
      } else {
        response = await instance.post(CREATE_BLOG, payload);
        navigate(`/blogs/${response.data._id}`, { replace: true });
      }

      const push = response?.data?.pushNotification;
      if (nextStatus === "published") {
        if (push?.sent) {
          // keep UX quiet on success
        } else if (push?.reason) {
          setError(
            `Post saved, but push was not sent: ${push.reason}`
          );
          return;
        }
      }

      if (isEdit) {
        navigate("/blogs");
      }
    } catch (err) {
      console.log(err);
      setError(
        err?.response?.data?.message || "Failed to save blog post."
      );
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="vehicles-page d-flex justify-content-center py-5">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Link to="/blogs">Blogs</Link>
        <Typography>{pageTitle}</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <ArticleOutlinedIcon fontSize="inherit" />
            {isEdit ? "Edit post" : "New post"}
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            {pageTitle}
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Write with a rich editor, set categories and tags, then draft or
            publish.
          </Typography>
        </div>
        <Button
          component={Link}
          to="/blogs"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.45)" }}
        >
          Back to list
        </Button>
      </section>

      {error ? (
        <Alert severity="error" className="mt-3">
          {error}
        </Alert>
      ) : null}

      <section className="section blog-editor mt-3">
        <div className="blog-editor-grid">
          <div className="blog-editor-main">
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => {
                const value = e.target.value;
                setTitle(value);
                if (!slugTouched) {
                  setSlug(slugify(value));
                }
              }}
              className="mb-3"
            />
            <TextField
              label="Slug"
              fullWidth
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(slugify(e.target.value));
              }}
              helperText="URL-friendly identifier"
              className="mb-3"
            />
            <TextField
              label="Excerpt"
              fullWidth
              multiline
              minRows={2}
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              className="mb-3"
            />

            <Typography className="vf-label mb-2">Content</Typography>
            <div className="blog-quill-wrap">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
              />
            </div>
          </div>

          <aside className="blog-editor-sidebar">
            <div className="blog-editor-panel">
              <Typography className="vf-section-title mb-2">Publish</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={status === "published"}
                    onChange={(e) =>
                      setStatus(e.target.checked ? "published" : "draft")
                    }
                  />
                }
                label={status === "published" ? "Published" : "Draft"}
              />
              <Chip
                size="small"
                label={status === "published" ? "Will publish" : "Saved as draft"}
                color={status === "published" ? "success" : "default"}
                className="mt-1"
              />
              <div className="blog-editor-actions mt-3">
                <LoadingButton
                  variant="outlined"
                  loading={saveLoading}
                  startIcon={<SaveOutlinedIcon />}
                  onClick={() => savePost("draft")}
                >
                  Save draft
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  loading={saveLoading}
                  onClick={() => savePost("published")}
                >
                  Publish
                </LoadingButton>
              </div>
            </div>

            <div className="blog-editor-panel">
              <Typography className="vf-section-title mb-2">
                Featured image
              </Typography>
              <ImageUploader
                uploadUrl={UPLOAD_BLOG_URL}
                image={featuredImage}
                onChange={(value) => setFeaturedImage(value?.fileName || "")}
              />
            </div>

            <div className="blog-editor-panel">
              <Typography className="vf-section-title mb-2">Categories</Typography>
              {categories.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No categories yet. Create some from the Blogs list.
                </Typography>
              ) : (
                <div className="blog-category-chips">
                  {categories.map((cat) => {
                    const selected = selectedCategories.includes(cat._id);
                    return (
                      <Chip
                        key={cat._id}
                        label={cat.name}
                        color={selected ? "primary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        onClick={() => toggleCategory(cat._id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="blog-editor-panel">
              <Typography className="vf-section-title mb-2">Tags</Typography>
              <Form.Control
                placeholder="ev, suv, launch"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary" className="mt-1 d-block">
                Comma-separated tags
              </Typography>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default BlogEditor;
